import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { css } from '@emotion/core';
import { faSignOutAlt } from '@fortawesome/pro-regular-svg-icons/faSignOutAlt';
import { IconButton } from '@veeva/corgix/button';
import Tooltip from '@veeva/corgix/tooltip';
import SiteLogo from '../../images/site-logo.svg';
import useMessageService from '../../services/i18n/useMessageService';
import HeaderTabList from './HeaderTabList';
import logout from '../../services/logout';
import { AuthenticatedPaths } from '../../constants/Paths';

const Header = ({ userSessionContext }) => {
    const { pathname } = useLocation();
    const [[logoutLabel, headerAltLabel]] = useMessageService([
        'general.logout',
        'general.veevaid',
    ]);

    const handleLogoutClick = () => {
        logout();
    };

    const headerCss = css`
        background-color: #223354;
        height: 4.25rem;
        display: flex;
        align-items: center;
        padding: 0 1rem;
        min-width: 65rem; // 65rem because our MyAccountLayouts has a min-width of 58.3333rem + 6.6666rem of padding

        button:not([disabled]) {
            border-radius: 100%;
            background-color: transparent;
            width: 3.667rem;
            height: 3.667rem;
            color: #ffffff;
            display: inline-block;
            margin-left: auto;

            svg {
                width: 2.333rem;
                height: 2.333rem;
                cursor: pointer;
                color: #ffffff;
            }

            &:hover,
            &:focus {
                background-color: rgba(255, 255, 255, 0.2);

                > svg:first-of-type {
                    color: #ffffff;
                }
            }

            &:active {
                background-color: rgba(255, 255, 255, 0.3);
            }
        }
    `;

    const headerTabListCss = css`
        display: flex;
        gap: 3rem;
        align-items: center;
        height: 100%;
    `;

    const headerLogoCss = css`
        width: 12.5rem;
        height: 2.167rem;
    `;

    if (pathname === AuthenticatedPaths.VAULT_INVITES) {
        return <></>;
    }

    if (userSessionContext?.loading) {
        return (
            <div css={headerCss}>
                <div css={headerTabListCss}>
                    <img css={headerLogoCss} src={SiteLogo} alt={headerAltLabel} />
                </div>
            </div>
        );
    }

    return (
        <div css={headerCss}>
            <div css={headerTabListCss}>
                <img css={headerLogoCss} src={SiteLogo} alt={headerAltLabel} />
                <HeaderTabList userSessionContext={userSessionContext} />
            </div>
            <div
                css={css`
                    flex-grow: 1;
                `}
            />
            <Tooltip placement="bottom" content={logoutLabel}>
                <IconButton onClick={handleLogoutClick} icon={faSignOutAlt} />
            </Tooltip>
        </div>
    );
};

Header.propTypes = {
    userSessionContext: PropTypes.shape({
        enableVeevaIdUsernameUpdate: PropTypes.bool,
        handleResponseSessionErrors: PropTypes.func,
        loading: PropTypes.bool,
        showStudiesTab: PropTypes.bool,
        systemsData: PropTypes.arrayOf(
            PropTypes.shape({
                appRedirectUrl: PropTypes.string,
                companyIdentifier: PropTypes.string,
                companyName: PropTypes.string,
                description: PropTypes.string,
                features: PropTypes.arrayOf(PropTypes.string),
                lastLogin: PropTypes.instanceOf(Date),
                lastLoginFormatted: PropTypes.string,
                name: PropTypes.string,
            }),
        ),
    }),
};

export default Header;
